/**
 * ConnectFlow — Facebook Messenger (Page-based OAuth).
 *
 * Cliente clica "Conectar via Facebook" → backend retorna authorize_url →
 * abre popup Meta → cliente loga no Facebook → autoriza → callback envia
 * postMessage de volta → frontend marca como conectado.
 *
 * Diferente do IG: qualquer Page Facebook que o user administra serve
 * (não exige IG Business vinculada).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  IconBrandMessenger,
  IconShieldCheck,
  IconMessage,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import useMessengerAccount from '../../../../../MessengerConfig/hooks/useMessengerAccount';
import MessengerApiService from '../../../../../MessengerConfig/services/MessengerApiService';
import '../../Instagram/ConnectFlow/connectFlow.css';

const STEPS = {
  INTRO: 'intro',
  REDIRECTING: 'redirecting',
  WAITING: 'waiting',
  SELECT_PAGE: 'select_page',
  SUCCESS: 'success',
  ERROR: 'error',
};

const BENEFITS = [
  { icon: IconShieldCheck, text: 'Login OAuth oficial via Facebook' },
  { icon: IconMessage, text: 'DMs do Messenger entram na Central de Atendimento' },
  { icon: IconBrandMessenger, text: 'IA responde DMs (configurável por agente)' },
];

const POPUP_FEATURES =
  'width=600,height=720,left=100,top=100,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes';

const MSG_BRAND = '#0084FF';

const ConnectFlow = ({ agentId, onConnected, onCancel }) => {
  const [step, setStep] = useState(STEPS.INTRO);
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState(null);
  const [pageCandidates, setPageCandidates] = useState([]);
  const [selectionKey, setSelectionKey] = useState('');
  const [submittingPage, setSubmittingPage] = useState(false);
  const popupRef = useRef(null);
  const popupTimerRef = useRef(null);
  const successHandledRef = useRef(false);
  const { oauthStart, fetchStatus } = useMessengerAccount(agentId);

  const handleSuccess = useCallback(
    (data) => {
      if (successHandledRef.current) return;
      successHandledRef.current = true;
      setResult({ page_name: data?.page_name || '' });
      setStep(STEPS.SUCCESS);
      fetchStatus();
    },
    [fetchStatus],
  );

  const cleanupPopup = useCallback(() => {
    if (popupTimerRef.current) {
      clearInterval(popupTimerRef.current);
      popupTimerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  useEffect(() => () => cleanupPopup(), [cleanupPopup]);

  // Listener do postMessage do callback (fast path)
  useEffect(() => {
    const handler = (event) => {
      const data = event.data;
      if (!data || data.type !== 'messenger_oauth_result') return;

      cleanupPopup();
      if (data.ok) {
        handleSuccess(data);
      } else if (data.needs_page_selection && Array.isArray(data.candidates)) {
        setPageCandidates(data.candidates);
        setSelectionKey(data.selection_key || '');
        setStep(STEPS.SELECT_PAGE);
      } else {
        setErrorMsg(data.error || 'Erro ao conectar Messenger.');
        setStep(STEPS.ERROR);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [cleanupPopup, handleSuccess]);

  // Polling de fallback quando COOP bloqueia postMessage
  useEffect(() => {
    if (step !== STEPS.WAITING) return undefined;

    let cancelled = false;
    const poll = async () => {
      if (cancelled || successHandledRef.current) return;
      try {
        const resp = await MessengerApiService.getStatus(agentId);
        if (!cancelled && resp?.data?.connected) {
          cleanupPopup();
          handleSuccess({ page_name: resp.data.page_name || '' });
        }
      } catch (err) {
        // ignora — próxima iteração tenta de novo
      }
    };

    const interval = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [step, agentId, cleanupPopup, handleSuccess]);

  const handleStart = async () => {
    setStep(STEPS.REDIRECTING);
    setErrorMsg('');
    try {
      const { authorize_url: authorizeUrl } = await oauthStart();
      if (!authorizeUrl) {
        throw new Error('Backend não retornou authorize_url');
      }

      const popup = window.open(authorizeUrl, 'msg_oauth', POPUP_FEATURES);
      if (!popup) {
        setStep(STEPS.ERROR);
        setErrorMsg(
          'Popup bloqueado pelo navegador. Permita popups e tente novamente.',
        );
        return;
      }

      popupRef.current = popup;
      setStep(STEPS.WAITING);

      popupTimerRef.current = setInterval(async () => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(popupTimerRef.current);
          popupTimerRef.current = null;

          if (successHandledRef.current) return;

          await new Promise((r) => setTimeout(r, 1500));
          try {
            const resp = await MessengerApiService.getStatus(agentId);
            if (resp?.data?.connected) {
              handleSuccess({ page_name: resp.data.page_name || '' });
              return;
            }
          } catch (err) {
            // ignora — vai cair em cancelamento
          }

          setStep((prev) => {
            if (prev === STEPS.WAITING) {
              setErrorMsg('Login cancelado.');
              return STEPS.ERROR;
            }
            return prev;
          });
        }
      }, 500);
    } catch (err) {
      setErrorMsg(
        err.response?.data?.error || err.message || 'Erro ao iniciar OAuth.',
      );
      setStep(STEPS.ERROR);
    }
  };

  const handleFinish = () => {
    if (onConnected) onConnected(result);
  };

  const handleSelectPage = async (pageId) => {
    if (!selectionKey || submittingPage) return;
    setSubmittingPage(true);
    try {
      const resp = await MessengerApiService.selectPage(
        agentId,
        selectionKey,
        pageId,
      );
      const data = resp?.data || {};
      handleSuccess({ page_name: data.page_name || '' });
    } catch (err) {
      setErrorMsg(
        err.response?.data?.error
          || 'Falha ao concluir conexão. Tente reconectar.',
      );
      setStep(STEPS.ERROR);
    } finally {
      setSubmittingPage(false);
    }
  };

  return (
    <div className="igconnect">
      {step === STEPS.INTRO && (
        <div className="igconnect__intro">
          <div
            className="igconnect__intro-icon"
            style={{ color: MSG_BRAND }}
          >
            <IconBrandMessenger size={32} />
          </div>
          <h3>Conectar Messenger</h3>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>
            Faça login com sua conta Facebook para conectar a Página
            que vai receber as mensagens no Messenger.
          </p>

          <div className="igconnect__benefits">
            {BENEFITS.map((b, i) => (
              <div key={i} className="igconnect__benefit">
                <b.icon size={16} style={{ color: MSG_BRAND }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          <div
            style={{
              padding: 12,
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 8,
              fontSize: 12,
              color: '#1d4ed8',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              marginTop: 16,
              textAlign: 'left',
            }}
          >
            <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              Pré-requisito: ter uma <strong>Página Facebook</strong> que você
              administre. Uma janela vai abrir para login no Facebook.
            </span>
          </div>

          <div
            style={{
              padding: 12,
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 8,
              fontSize: 12,
              color: '#78350f',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              marginTop: 8,
              textAlign: 'left',
            }}
          >
            <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              Durante o login do Facebook, na tela &ldquo;Quais Páginas você
              quer usar com o Mercado Agentes?&rdquo;, selecione{' '}
              <strong>apenas 1 Página</strong>. Marcar mais de uma faz a
              conexão falhar.
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 24,
            }}
          >
            <button type="button" onClick={onCancel} className="cloudapi-hero__action-btn">
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleStart}
              className="igconnect__primary-btn"
              style={{ background: MSG_BRAND }}
            >
              <IconBrandMessenger size={16} style={{ marginRight: 6 }} />
              Conectar via Facebook
            </button>
          </div>
        </div>
      )}

      {step === STEPS.REDIRECTING && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spinner animation="border" style={{ color: MSG_BRAND }} />
          <p style={{ marginTop: 16, fontSize: 14, color: '#374151' }}>
            Abrindo Facebook...
          </p>
        </div>
      )}

      {step === STEPS.WAITING && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spinner animation="border" style={{ color: MSG_BRAND }} />
          <p style={{ marginTop: 16, fontSize: 14, color: '#374151' }}>
            Aguardando autorização no Facebook...
          </p>
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
            Conclua o login na janela aberta.
          </p>
          <button
            type="button"
            onClick={() => {
              cleanupPopup();
              setStep(STEPS.INTRO);
            }}
            className="cloudapi-hero__action-btn"
            style={{ marginTop: 16 }}
          >
            Cancelar
          </button>
        </div>
      )}

      {step === STEPS.SELECT_PAGE && (
        <div style={{ padding: 24 }}>
          <h4 style={{ marginBottom: 8 }}>Escolha qual Página conectar</h4>
          <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
            Você administra várias Páginas Facebook. Selecione qual deseja
            conectar a este agente:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pageCandidates.map((c) => (
              <button
                key={c.page_id}
                type="button"
                disabled={submittingPage}
                onClick={() => handleSelectPage(c.page_id)}
                className="igconnect__primary-btn"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  padding: '12px 16px',
                  background: '#fff',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  cursor: submittingPage ? 'wait' : 'pointer',
                  opacity: submittingPage ? 0.6 : 1,
                }}
              >
                <strong>{c.page_name}</strong>
                {c.page_category && (
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    {c.page_category}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="cloudapi-hero__action-btn"
            style={{ marginTop: 16 }}
            disabled={submittingPage}
          >
            Cancelar
          </button>
        </div>
      )}

      {step === STEPS.SUCCESS && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#dcfce7',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a',
            }}
          >
            <IconCheck size={32} />
          </div>
          <h4 style={{ marginBottom: 8 }}>Conectado com sucesso!</h4>
          {result?.page_name && (
            <p style={{ fontSize: 14, color: '#374151' }}>{result.page_name}</p>
          )}
          <button
            type="button"
            onClick={handleFinish}
            className="igconnect__primary-btn"
            style={{ marginTop: 16, background: MSG_BRAND }}
          >
            Continuar
          </button>
        </div>
      )}

      {step === STEPS.ERROR && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#fef2f2',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
            }}
          >
            <IconAlertTriangle size={32} />
          </div>
          <h4 style={{ marginBottom: 8 }}>
            {/Múltiplas Páginas/i.test(errorMsg)
              ? 'Selecione apenas 1 Página'
              : 'Erro ao conectar'}
          </h4>
          <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 13 }}>
            {errorMsg}
          </p>
          {/Múltiplas Páginas/i.test(errorMsg) && (
            <div
              style={{
                padding: 12,
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 8,
                fontSize: 13,
                color: '#78350f',
                textAlign: 'left',
                marginBottom: 16,
              }}
            >
              <strong>Como resolver:</strong>
              <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                <li>Clique em <strong>Tentar novamente</strong>.</li>
                <li>Quando o Facebook abrir, clique em <em>Editar configurações</em> ou <em>Escolher quais Páginas você quer usar</em>.</li>
                <li>
                  <strong>Desmarque todas as Páginas</strong> e deixe selecionada{' '}
                  <strong>apenas 1</strong> — a que você quer conectar a este agente.
                </li>
                <li>Conclua o login.</li>
              </ol>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={onCancel}
              className="cloudapi-hero__action-btn"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => setStep(STEPS.INTRO)}
              className="igconnect__primary-btn"
              style={{ background: MSG_BRAND }}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectFlow;
